import { useGT } from 'gt-react';
import type { AgentPageStatus } from '@erp/rpc';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { agentPageBadgeState } from '@/routing/agent-pages-nav';

/**
 * Sidebar marker for an agent page: a pulsing dot while the agent's latest
 * run is in progress, a red dot when the latest run failed, nothing when idle.
 */
export function AgentPageStatusBadge({ status }: { status: AgentPageStatus }) {
  const gt = useGT();
  const state = agentPageBadgeState(status);
  if (!state) return null;

  const label =
    state.kind === 'running'
      ? gt('Agent is running', {
          context: 'sidebar marker on an agent page whose agent has a live run',
        })
      : gt('Last run failed', {
          context: 'sidebar marker on an agent page whose latest run failed',
        });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A button so keyboard users can focus it; the dot keeps a 20px hit target. */}
        <button
          type="button"
          aria-label={label}
          className="ring-sidebar-ring flex size-5 cursor-default items-center justify-center rounded-full outline-hidden focus-visible:ring-2"
        >
          <span
            aria-hidden="true"
            className={cn(
              'block size-2 rounded-full',
              state.kind === 'running'
                ? 'animate-pulse bg-emerald-500'
                : 'bg-red-500',
            )}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
